import { useEffect, useState } from 'react';
import { Field, useFormikContext } from 'formik';
import PropTypes from 'prop-types';
import { trackError } from 'utils/helpers';
import { useLazyScheduledTripAvailabilityQuery } from 'redux/api_slice/availability';
import GoogleMapLink from './google_map_link';
import { generateTitle, sortLocations } from './helpers';

const PickUpDropOff = ({ locations, bookableOption = {} }) => {
  const { values, setFieldValue } = useFormikContext();
  const [pickUps, setPickUps] = useState(locations[0]);
  const dropOffs = locations[1];

  // Handle Google Map Link updates
  const [selectedPickUpOption, setSelectedPickUpOption] = useState(() =>
    pickUps?.options.find((option) => option.id === values.pickUp)
  );
  const [selectedDropOffOption, setSelectedDropOffOption] = useState(() =>
    dropOffs?.options.find((option) => option.id === values.dropOff)
  );

  /*
   * Special case for Fraser Explorer Tours
   * Fraser has two pick-up routes - Inskip and River Heads, and only one route is allowed to be selected on a given date.
   * If the tide is high, the Inskip route is closed and the River Heads route is open, otherwise the River Heads route is closed and the Inskip route is open.
   */
  const [availableRouteId, setAvailableRouteId] = useState(null);
  const { pickUpRouteIds, isFraserKingTidePackage } = bookableOption;
  const [getScheduledTripAvailability, queryState] =
    useLazyScheduledTripAvailabilityQuery();

  useEffect(() => {
    if (!availableRouteId || !pickUps?.options) {
      return;
    }

    const fullPickUpLocations = locations[0].options;
    const availablePickUpOptions = fullPickUpLocations.filter((option) => {
      if (!option.bookable_online) {
        return false;
      }

      return option.route_id === availableRouteId;
    });

    setPickUps({
      ...pickUps,
      options: availablePickUpOptions,
    });

    // Reset pick-up selection
    setFieldValue('pickUp', availablePickUpOptions[0].id);
    setSelectedPickUpOption(availablePickUpOptions[0]);
  }, [availableRouteId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!values.date || !isFraserKingTidePackage) {
      return;
    }

    // Fetch route availability for the selected date
    (async () => {
      try {
        const availability = await getScheduledTripAvailability({
          route_id: pickUpRouteIds[0], // If the first route is closed, the second route is open and vice versa
          start_date: values.date,
          end_date: values.date,
        });
        const response = availability.data[values.date];

        // Empty array means the route is closed
        setAvailableRouteId(
          response.length ? pickUpRouteIds[0] : pickUpRouteIds[1]
        );
      } catch (error) {
        setPickUps(null);
        setAvailableRouteId(null);
        trackError(error);
      }
    })();
  }, [values.date]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <div className="border-top">
        <h4 className="my-4">{generateTitle({ pickUps, dropOffs })}</h4>
        <span>
          Please note pick-up and drop-off times are approximate and can vary by
          up to 15 minutes.
        </span>
      </div>
      {pickUps && (
        <div className="py-4 space-y-2">
          <label className="fw-bold" htmlFor="pickUp">
            {pickUps.label}
          </label>
          <Field
            name="pickUp"
            id="pickUp"
            className="form-select"
            component="select"
            disabled={queryState.isFetching}
            onChange={(event) => {
              const value = Number(event.target.value);

              setFieldValue('pickUp', value);
              setSelectedPickUpOption(
                pickUps.options.find((option) => option.id === value)
              );
            }}
          >
            {sortLocations(pickUps.options).map((item) => (
              <option value={item.id} key={item.id}>
                {item.name}
              </option>
            ))}
          </Field>
          <GoogleMapLink option={selectedPickUpOption} />
        </div>
      )}
      {dropOffs && (
        <div className="pb-4 space-y-2">
          <label className="fw-bold" htmlFor="dropOff">
            {dropOffs.label}
          </label>
          <Field
            name="dropOff"
            id="dropOff"
            className="form-select"
            component="select"
            onChange={(event) => {
              const value = Number(event.target.value);

              setFieldValue('dropOff', value);
              setSelectedDropOffOption(
                dropOffs.options.find((option) => option.id === value)
              );
            }}
          >
            {sortLocations(dropOffs.options)
              .filter((option) => option.bookable_online === true)
              .map((item) => (
                <option value={item.id} key={item.id}>
                  {item.name}
                </option>
              ))}
          </Field>
          <GoogleMapLink option={selectedDropOffOption} />
        </div>
      )}
    </>
  );
};

const optionShape = PropTypes.shape({
  id: PropTypes.number,
  name: PropTypes.string,
  address: PropTypes.string,
});

PickUpDropOff.propTypes = {
  locations: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      options: PropTypes.arrayOf(optionShape),
    })
  ),
  bookableOption: PropTypes.shape({
    pickUpRouteIds: PropTypes.arrayOf(PropTypes.number),
    isFraserKingTidePackage: PropTypes.bool,
  }),
};

export default PickUpDropOff;
